import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Paper, TextField, InputAdornment, Chip,
  Stack, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  IconButton, Menu, MenuItem, FormControl, Select, Alert, CircularProgress,
  Card, CardContent, Badge, Tooltip, Divider, Container, CssBaseline, alpha
  } from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Inventory as InventoryIcon,
  Refresh as RefreshIcon,
  CreditScore,
  Edit as EditIcon
} from '@mui/icons-material';
import AppNavbar from '../../../components/AppNavbar';
import Header from '../../../components/Header';
import SideMenu from '../../../components/SideMenu';
import AppTheme from '../../../shared-theme/AppTheme';
import { StyledCellHeader } from '../../../components/StyledCellHeader';
import CrearRecuperacionDialog from "./crearrecuperaciondialog";
import EditarRecuperacionDialog from "./editarrecuperaciondialog";
import AlertMessageDialog from '../../../components/Dialogs/alertMessageDialog';
import OnGeneralMessageDialog from '../../../components/Dialogs/OnGeneralMessageDialog';
import AccessDeniedBox from '../../../components/AccessDeniedBox';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../../dashboard/theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const RECOVERY_STATUSES = [
  { id: "en_proceso", name: "En Proceso", color: "info", icon: <AssignmentIcon /> },
  { id: "completada", name: "Completada", color: "success", icon: <CheckCircleIcon /> },
  { id: "cancelada", name: "Cancelada", color: "error", icon: <CancelIcon /> },
  { id: "suspendida", name: "Suspendida", color: "default", icon: <WarningIcon /> },
  { id: "pagado", name: "Pagado", color: "success", icon: <CreditScore /> }
];

const RECOVERY_PRIORITIES = [
  { id: "baja", name: "Baja", color: "success" },
  { id: "media", name: "Media", color: "warning" },
  { id: "alta", name: "Alta", color: "error" },
  { id: "urgente", name: "Urgente", color: "error" }
];

// Mock data for demonstration
const MOCK_RECOVERY_REQUESTS = [
  {
    id: 1,
    cliente: { nombre: "Juan Pérez", identificacion: "402-1234567-8" },
    producto: { nombre: "Toyota Corolla 2020", id: "PROD001" },
    prestamo_id: "PREST001",
    motivo: "impago",
    prioridad: "alta",
    descripcion: "Cliente no ha pagado las últimas 3 cuotas",
    ubicacion_actual: "Calle Principal #123, Santo Domingo",
    contacto_alternativo: "809-555-0123",
    observaciones: "Cliente ha sido contactado múltiples veces",
    monto_recuperacion: 250000,
    status: "pendiente",
    created_at: "2024-01-15T10:30:00Z",
    created_by: "Usuario 1"
  },
  {
    id: 2,
    cliente: { nombre: "María García", identificacion: "402-9876543-2" },
    producto: { nombre: "Honda Civic 2019", id: "PROD002" },
    prestamo_id: "PREST002",
    motivo: "vencimiento",
    prioridad: "urgente",
    descripcion: "Préstamo vencido hace 2 meses",
    ubicacion_actual: "Avenida Central #456, Santiago",
    contacto_alternativo: "809-555-0456",
    observaciones: "Producto en buen estado",
    monto_recuperacion: 180000,
    status: "en_proceso",
    created_at: "2024-01-14T14:20:00Z",
    created_by: "Usuario 2"
  },
  {
    id: 3,
    cliente: { nombre: "Carlos Rodríguez", identificacion: "402-5555555-5" },
    producto: { nombre: "Nissan Sentra 2021", id: "PROD003" },
    prestamo_id: "PREST003",
    motivo: "voluntario",
    prioridad: "baja",
    descripcion: "Cliente solicita recuperación voluntaria",
    ubicacion_actual: "Calle Secundaria #789, La Romana",
    contacto_alternativo: "809-555-0789",
    observaciones: "Cliente cooperativo",
    monto_recuperacion: 320000,
    status: "completada",
    created_at: "2024-01-13T09:15:00Z",
    created_by: "Usuario 1"
  }
];

const RecuperacionScreen = (props) => {
  const [recoveryRequests, setRecoveryRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [priorityFilter, setPriorityFilter] = useState("todos");
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedRecuperacion, setSelectedRecuperacion] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [selectedRequestForStatus, setSelectedRequestForStatus] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [confirmStatusDialogOpen, setConfirmStatusDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [filteredStatuses, setFilteredStatuses] = useState(RECOVERY_STATUSES);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    fetchRecoveryRequests();
  }, []);

  const fetchRecoveryRequests = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/recuperacion/solicitudes?idcompany=${localStorage.getItem('idcompany')}&id_sucursal=${localStorage.getItem('id_sucursal')}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });
      
      if (response.ok) {
      const data = await response.json();
        setRecoveryRequests(data);
        setAccessDenied(false);
      } else {
        // Use mock data for demonstration
        setAccessDenied(true);
      }
    } catch (error) {
      console.error('Error fetching recovery requests:', error);
      // Use mock data for demonstration
      setAccessDenied(true);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/recuperacion/status/${requestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify({
          status: newStatus,
          idcompany: localStorage.getItem('idcompany'),
          producto_id: selectedRequestForStatus.producto.nombre
        })
      });

      if (response.ok) {
        setRecoveryRequests(prev => 
          prev.map(request => 
            request.id === requestId 
              ? { ...request, status: newStatus }
              : request
          )
        );
        
        setAlertTitle("Éxito");
        setAlertMessage("Estado actualizado correctamente");
        setAlertSeverity("success");
        setAlertOpen(true);
    } else {
        throw new Error('Error al actualizar el estado');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setAlertTitle("Error");
      setAlertMessage("Error al actualizar el estado");
      setAlertSeverity("error");
      setAlertOpen(true);
    }
    
    setStatusMenuAnchor(null);
    setSelectedRequestForStatus(null);
    setConfirmStatusDialogOpen(false);
    setPendingStatusChange(null);
  };

  const openStatusMenu = (event, request) => {
    if(request.status == "completada"){
        setFilteredStatuses(RECOVERY_STATUSES.filter(status => status.id != "completada" && status.id != "suspendida" && status.id != "en_proceso" && status.id != "pagado"));
    }
    else if(request.status == "pagado"){
      setFilteredStatuses([]);
    }
    else{
        setFilteredStatuses(RECOVERY_STATUSES.filter(status => status.id != "pagado"));
    }
    setStatusMenuAnchor(event.currentTarget);
    setSelectedRequestForStatus(request);
  };

  const closeStatusMenu = () => {
    setStatusMenuAnchor(null);
//    setSelectedRequestForStatus(null);
  };

  const handleStatusMenuClick = (status) => {
    setPendingStatusChange({ requestId: selectedRequestForStatus.id, newStatus: status.id });
    setConfirmStatusDialogOpen(true);
    closeStatusMenu();
  };

  const handleConfirmStatusChange = () => {
    if (pendingStatusChange) {
      handleStatusChange(pendingStatusChange.requestId, pendingStatusChange.newStatus);
    }
  };

  const handleCancelStatusChange = () => {
    setConfirmStatusDialogOpen(false);
    setPendingStatusChange(null);
  };

  const getStatusInfo = (status) => {
    return RECOVERY_STATUSES.find(s => s.id === status) || RECOVERY_STATUSES[0];
  };

  const getPriorityInfo = (priority) => {
    return RECOVERY_PRIORITIES.find(p => p.id === priority) || RECOVERY_PRIORITIES[1];
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-DO", { 
      style: "currency", 
      currency: "DOP" 
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-DO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredRequests = recoveryRequests.filter(request => {
    const matchesSearch = 
      request.cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.cliente.identificacion.includes(searchTerm) ||
      request.producto.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.prestamo_id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "todos" || request.status === statusFilter;
    const matchesPriority = priorityFilter === "todos" || request.prioridad === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const handleCreateSuccess = (newRequest) => {
    setRecoveryRequests(prev => [newRequest, ...prev]);
    setAlertTitle("Éxito");
    setAlertMessage("Solicitud de recuperación creada correctamente");
    setAlertSeverity("success");
    setAlertOpen(true);
  };

  const handleEditRecuperacion = (recuperacion) => {
    setSelectedRecuperacion(recuperacion);
    setOpenEditDialog(true);
  };

  const handleEditSuccess = (updatedRecuperacion) => {
    setRecoveryRequests(prev => 
      prev.map(req => req.id === updatedRecuperacion.id ? updatedRecuperacion : req)
    );
    setAlertTitle("Éxito");
    setAlertMessage("Recuperación actualizada correctamente");
    setAlertSeverity("success");
    setAlertOpen(true);
    setOpenEditDialog(false);
    setSelectedRecuperacion(null);
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        {/* Main content */}
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
              mx: 3,
              pb: 5,
              mt: { xs: 8, md: 0 },
            }}
          >
            <Header parent={"Gestión Comercial"} child={"Recuperación"} />
            {!accessDenied ? 
            <Container maxWidth={false} sx={{ px: 5, py: 5 }}>
              <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', fontFamily: 'Inter, "Noto Sans", sans-serif' }}>
                <Box maxWidth="lg" mx="auto" py={4}>
                  {/* Header */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" px={2} mb={2}>
                    <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon color="warning" />
                      Gestión de Recuperaciones
                    </Typography>
                    <Button
                      startIcon={<AddIcon />}
                      variant="contained"
                      sx={{ fontWeight: "bold" }}
                      color="primarybutton"
                      onClick={() => setOpenCreateDialog(true)}
                    >
                      Nueva Solicitud
                    </Button>
                  </Box>

                  {/* Filters */}
                  <Paper sx={{ p: 2, mb: 3, mx: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          placeholder="Buscar por cliente, producto o préstamo..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="todos">Todos los estados</MenuItem>
                            {RECOVERY_STATUSES.map((status) => (
                              <MenuItem key={status.id} value={status.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {status.icon}
                                  {status.name}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <Select
                            value={priorityFilter}
                            onChange={(e) => setPriorityFilter(e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="todos">Todas las prioridades</MenuItem>
                            {RECOVERY_PRIORITIES.map((priority) => (
                              <MenuItem key={priority.id} value={priority.id}>
                                <Chip 
                                  label={priority.name} 
                                  color={priority.color} 
                                  size="small" 
                                  sx={{ mr: 1 }}
                                />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<RefreshIcon />}
                          onClick={fetchRecoveryRequests}
                          disabled={loading}
                        >
                          Actualizar
                        </Button>
                    </Grid>
                    </Grid>
                  </Paper>

                  {/* Recovery Requests Table */}
                  <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', mx: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <StyledCellHeader>Cliente</StyledCellHeader>
                          <StyledCellHeader>Producto</StyledCellHeader>
                          <StyledCellHeader>Préstamo</StyledCellHeader>
                          <StyledCellHeader>Prioridad</StyledCellHeader>
                          <StyledCellHeader>Estado</StyledCellHeader>
                          <StyledCellHeader>Monto</StyledCellHeader>
                          <StyledCellHeader>Ubicación</StyledCellHeader>
                          <StyledCellHeader>Fecha</StyledCellHeader>
                          <StyledCellHeader>Acciones</StyledCellHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                              <CircularProgress />
                            </TableCell>
                          </TableRow>
                        ) : filteredRequests.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                              <Typography variant="h6" color="text.secondary">
                                No se encontraron solicitudes de recuperación
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredRequests.map((request) => {
                            const statusInfo = getStatusInfo(request.status);
                            const priorityInfo = getPriorityInfo(request.prioridad);
                            
                            return (
                              <TableRow key={request.id} hover>
                                <TableCell>
                                  <Box>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                      {request.cliente.nombre}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {request.cliente.identificacion}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                      {request.producto.nombre}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={600}>
                                    {request.prestamo_id}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={priorityInfo.name} 
                                    color={priorityInfo.color} 
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={statusInfo.name} 
                                    color={statusInfo.color} 
                                    size="small"
                                    icon={statusInfo.icon}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={600}>
                                    {formatCurrency(request.monto_recuperacion)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Tooltip title={request.ubicacion_actual}>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                      {request.ubicacion_actual}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {formatDate(request.created_at)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box display="flex" gap={1}>
                                    <IconButton
                                      onClick={() => handleEditRecuperacion(request)}
                                      size="small"
                                      sx={{ color: 'primary.main' }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      onClick={(e) => openStatusMenu(e, request)}
                                      size="small"
                                    >
                                      <MoreVertIcon />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </Container>
            :
            <Box>
              <br></br>
              <br></br>
            <AccessDeniedBox
              title={'Acceso denegado'}
              message={'No tienes permisos para ver esta sección de Recuperación. Contacta a tu administrador para solicitar acceso o verifica tus roles y permisos.'}
            />
            </Box>
            }
          </Stack>
        </Box>
      </Box>

      {/* Status Change Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={closeStatusMenu}
      >
        {filteredStatuses.map((status) => (
          <MenuItem
            key={status.id}
            onClick={() => handleStatusMenuClick(status)}
            disabled={selectedRequestForStatus?.status === status.id}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {status.icon}
              {status.name}
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Create Recovery Dialog */}
      <CrearRecuperacionDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        onConfirm={handleCreateSuccess}
        solicitudesActivas={recoveryRequests.filter(request => request.status === "pendiente" || request.status === "en_proceso" || request.producto.estado == "incautado")}
      />

      {/* Edit Recovery Dialog */}
      <EditarRecuperacionDialog
        open={openEditDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setSelectedRecuperacion(null);
        }}
        onConfirm={handleEditSuccess}
        recuperacion={selectedRecuperacion}
      />

      {/* Alert Dialog */}
      <AlertMessageDialog
        open={alertOpen}
        setOpen={setAlertOpen}
        handleClose={handleAlertClose}
        title={alertTitle}
        message={alertMessage}
        severity={alertSeverity}
      />

      {/* Status Change Confirmation Dialog */}
      <OnGeneralMessageDialog
        open={confirmStatusDialogOpen}
        onClose={handleCancelStatusChange}
        onRegister={handleConfirmStatusChange}
        title="Confirmar Cambio de Estado"
        message={`¿Estás seguro de que deseas cambiar el estado de la solicitud de recuperación a "${pendingStatusChange ? getStatusInfo(pendingStatusChange.newStatus).name : ''}"?`}
        buttonText="Confirmar Cambio"
        buttonColor="primarybutton"
      />
    </AppTheme>
  );
};

export default RecuperacionScreen;
